<template>
  <div class="critical-hour-distribution">
    <accordion-panel
      id="critical-hour-distribution"
      class="mt-3"
      :collapsed.sync="collapsed"
    >
      <template #title>
        <label class="m-0">
          {{ title }}
        </label>
        <info-badge
          class="critical-hour-distribution__title-tooltip"
          :string-key="titleTooltip"
        />
      </template>
      <s-loader
        v-show="loading"
        class="py-3"
        size="lg"
      />
      <div v-show="!loading && chartData.length">
        <div class="critical-hour-distribution__legend-box my-2">
          <div
            v-for="legendItem in legend"
            class="critical-hour-distribution__legend-item"
            :key="legendItem.windingId"
          >
            <i
              class="fas fa-square-full critical-hour-distribution__legend-color"
              aria-hidden="true"
              :style="`color: ${legendItem.color}`"
            />
            <label class="m-0">
              {{ $t('general.winding') }} {{ legendItem.windingId }}
            </label>
          </div>
        </div>
        <div class="critical-hour-distribution__chart-wrapper">
          <e-chart
            class="critical-hour-distribution__chart"
            ref="echarts"
            autoresize
            :options="options"
            @rendered="onRendered"
          />
          <img
            class="critical-hour-distribution__image w-100"
            alt="chart-image"
            :src="chartImg"
          >
        </div>
        <div class="critical-hour-distribution__footer-message">
          {{ $t('engineering.isolation-aging.histogram-chart-message') }}
        </div>
      </div>
      <s-empty-list
        v-show="!loading && !chartData.length"
      />
    </accordion-panel>
  </div>
</template>

<script>
import AccordionPanel from '@/components/AccordionPanel';
import InfoBadge from '@/components/icon/InfoBadge';
import themeService from '@/services/themeService';
import { chartColors } from '@/domain/data/charts/chartColors';
import { mapGetters } from 'vuex';
import { formatNumber } from '@/services/translate';

export default {
  name: 'CriticalHourDistributionChart',
  props: {
    chartData: {
      type: Array,
      default: () => ([]),
    },
    occurrences: {
      type: Number,
      default: 5,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    installationTimeZone: {
      type: String,
      default: '',
    },
  },
  components: {
    AccordionPanel,
    InfoBadge,
  },
  data: () => ({
    collapsed: false,
    chartImg: null,
  }),
  computed: {
    ...mapGetters([
      'isPrinting',
    ]),
    title() {
      if (this.occurrences === 1) {
        return this.$t('engineering.isolation-aging.single-critical-hour-distribution');
      }
      return this.$t('engineering.isolation-aging.critical-hour-distribution', { occurrences: this.occurrences });
    },
    titleTooltip() {
      return this.$t('messages.MI039.message', { timezone: this.installationTimeZone });
    },
    axisNotation() {
      const itemWithNotation = this.chartData.find(item => item.notation) || {};
      return itemWithNotation.notation;
    },
    legend() {
      const windings = new Set();
      const legend = [];
      this.chartData.forEach((data) => {
        windings.add(data.windingId);
      });
      windings.forEach((winding) => {
        legend.push({
          color: this.getSeriesColor({ windingId: winding }),
          windingId: winding,
        });
      });
      return legend;
    },
    formattedData() {
      return this.chartData.map((data) => {
        const values = (data.values || []).map((value) => {
          const hour = value.x.split(':');
          return [
            `${hour[0]}:00 - ${hour[0]}:59`,
            value.y,
          ];
        });
        values.sort((first, second) => first[0].localeCompare(second[0]));
        return {
          ...data,
          values,
        };
      });
    },
    allXValues() {
      const allXValues = new Set();
      this.formattedData.forEach((item) => {
        item.values.forEach((value) => {
          allXValues.add(value[0]);
        });
      });
      const allValuesArray = Array.from(allXValues);
      allValuesArray.sort((first, second) => first.localeCompare(second));
      return allValuesArray;
    },
    fontColor() {
      return this.isPrinting
        ? themeService.getStyleVar('color-gray-medium-dark')
        : themeService.getFontColor();
    },
    options() {
      return {
        tooltip: {
          show: true,
          contain: true,
          formatter: this.getTooltipFormatter,
          borderColor: themeService.getStyleVar('color-tooltip-border'),
        },
        backgroundColor: this.isPrinting
          ? themeService.getStyleVar('chart-print-background')
          : themeService.getStyleVar('color-background-dark'),
        grid: {
          top: 40,
          right: '5%',
          bottom: 45,
          left: '5%',
        },
        xAxis: {
          type: 'category',
          data: this.allXValues,
          axisLine: {
            show: true,
            lineStyle: {
              color: this.fontColor,
            },
          },
        },
        yAxis: {
          name: this.axisNotation,
          type: 'value',
          boundaryGap: '10%',
          splitNumber: 3,
          splitLine: {
            show: false,
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: this.fontColor,
            },
          },
          axisLabel: {
            showMinLabel: false,
            formatter: value => formatNumber(value),
          },
        },
        series: this.formattedData.map(data => ({
          data: data.values,
          type: 'bar',
          barGap: '0',
          itemStyle: {
            color: this.getSeriesColor(data),
            borderColor: '#fff',
            borderWidth: 1,
          },
        })),
      };
    },
  },
  methods: {
    getSeriesColor(item) {
      const COLORS_LIMIT = 40;
      const randomColor = chartColors[Math.floor(Math.random() * COLORS_LIMIT)];
      const colorsByWinding = {
        1: this.fontColor,
        2: themeService.getPrimaryColor(),
        3: themeService.getStyleVar('color-link'),
      };
      return colorsByWinding[item.windingId] || randomColor;
    },
    getTooltipFormatter(params, ticket, callback) {
      const tooltipValue = `${params.data[0]}&ensp;-&ensp;${params.data[1]}`;
      callback(ticket, tooltipValue);
    },
    onRendered() {
      this.$nextTick(() => {
        this.chartImg = this.isCollapsed || !this.$refs.echarts.height
          ? '#'
          : this.$refs.echarts.getDataURL();
      });
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.critical-hour-distribution
  &__chart
    width: 100%
    height: 250px
    border: 1px solid $color-border-gray
    border-radius: 3px
    +media-max-md
      min-width: 800px

  &__title-tooltip
    z-index: 2
    margin-left: 5px

  &__legend-box
    display: flex
    +media-max-xs
      display: block

  &__legend-item
    position: relative
    min-width: 33.33%
    display: flex
    align-items: center
    justify-content: center
    &:nth-child(1)
      justify-content: flex-start
    &:nth-child(3)
      justify-content: flex-end
    +media-max-xs
      display: block

  &__chart-wrapper
    overflow-x: auto

  &__legend
    margin: 12px 0 14px

  &__legend-color
    margin-right: 5px
    font-size: 1.1em

  &__footer-message
    font-weight: bold
    width: 100%
    margin-top: 10px
    font-size: .8rem
    text-align: right

  ::v-deep .accordion-panel
    box-shadow: none

    &__title-container
      display: flex
      align-items: center

    &__header
      padding: 3px 15px !important

  &__image
    display: none

@media print
  .critical-hour-distribution
    &__chart
      display: none

    &__image
      display: block
</style>
